import { FC, useState } from "react";
import { Icon } from "@/components/Common/Icon";
import { IconNames } from "@/types/v2";
import { cn } from "@/lib/utils";
import { SortCriteria, ColumnConfig } from "../types";
import { Button } from "@/components/Base/Button";
import { Tooltip } from "@/components/Common/Tooltip";
import { getColumnDisplayLabel } from "../hooks/useEnhancedColumns";

interface SortManagementPanelProps {
  sortCriteria: SortCriteria[];
  columns: ColumnConfig[];
  onReorder: (fromIndex: number, toIndex: number) => void;
  onRemove: (field: string) => void;
  onClear: () => void;
  className?: string;
}

interface DragState {
  draggedIndex: number | null;
  dropTargetIndex: number | null;
}

export const SortManagementPanel: FC<SortManagementPanelProps> = ({
  sortCriteria,
  columns,
  onReorder,
  onRemove,
  onClear,
  className,
}) => {
  const [dragState, setDragState] = useState<DragState>({
    draggedIndex: null,
    dropTargetIndex: null,
  });

  // Always render the panel, even if no sort criteria (for popover usage)
  const hasSortCriteria = sortCriteria.length > 0;

  // Get column label for a field
  const getColumnLabel = (field: string): string => {
    const column = columns.find((col) => col.field === field);
    return column ? getColumnDisplayLabel(column) : String(field);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDragState({ draggedIndex: index, dropTargetIndex: null });
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragState((prev) => ({ ...prev, dropTargetIndex: index }));
  };

  const handleDragLeave = (e: React.DragEvent) => {
    // Only clear if we're actually leaving the element
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragState((prev) => ({ ...prev, dropTargetIndex: null }));
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    const dragIndex = dragState.draggedIndex;

    if (dragIndex !== null && dragIndex !== dropIndex) {
      onReorder(dragIndex, dropIndex);
    }

    setDragState({ draggedIndex: null, dropTargetIndex: null });
  };

  const handleDragEnd = () => {
    setDragState({ draggedIndex: null, dropTargetIndex: null });
  };

  return (
    <div
      className={cn("rounded-lg border border-border bg-card p-3", className)}
    >
      {hasSortCriteria ? (
        <>
          <div className="mb-2 flex items-center justify-between">
            <h3 className="relative w-full text-sm font-medium text-card-foreground">
              Sort Order{" "}
              {sortCriteria.length > 1 && `(${sortCriteria.length} levels)`}
              {sortCriteria.length > 1 && (
                <Tooltip
                  asChild={false}
                  message={
                    "Drag to reorder priority. Hold Ctrl/Cmd when clicking headers to\n" +
                    "              add more levels."
                  }
                >
                  <Icon
                    name={IconNames.info}
                    className="ml-2 text-sm text-muted-foreground"
                    size={14}
                  />
                </Tooltip>
              )}
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClear}
              className="h-6 px-2 text-xs"
            >
              <Icon name={IconNames.clear} size="xs" className="mr-1" />
              Clear All
            </Button>
          </div>

          <div className="flex flex-row flex-wrap gap-2">
            {sortCriteria
              .sort((a, b) => a.priority - b.priority)
              .map((criteria, index) => (
                <div
                  key={`${criteria.field}-${criteria.priority}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, index)}
                  onDragEnd={handleDragEnd}
                  className={cn(
                    "flex items-center justify-between rounded-md border px-1 py-1 text-sm transition-colors",
                    "cursor-move hover:bg-muted/50",
                    dragState.draggedIndex === index && "opacity-50",
                    dragState.dropTargetIndex === index && "ring-2 ring-primary"
                  )}
                >
                  <div className="flex items-center gap-1">
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-primary-foreground">
                      {index + 1}
                    </span>
                    <span className="font-medium text-primary">
                      {getColumnLabel(criteria.field)}
                    </span>
                    <Icon
                      name={
                        criteria.direction === "asc"
                          ? IconNames.upArrow
                          : IconNames.downArrow
                      }
                      size={14}
                      className="text-muted-foreground"
                    />
                    <span className="text-xs text-muted-foreground">
                      {criteria.direction === "asc"
                        ? "Ascending"
                        : "Descending"}
                    </span>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(criteria.field)}
                    className="h-6 w-6 p-0 hover:bg-destructive/10 hover:text-destructive"
                  >
                    <Icon name={IconNames.x} size={16} />
                  </Button>
                </div>
              ))}
          </div>
        </>
      ) : (
        <div className="py-4 text-center">
          <div className="mb-2">
            <Icon
              name={IconNames.sort}
              size={32}
              className="mx-auto text-muted-foreground"
            />
          </div>
          <h3 className="mb-1 text-sm font-medium text-card-foreground">
            No sorting applied
          </h3>
          <p className="text-xs text-muted-foreground">
            Click column headers to add sort criteria.
            <br />
            Hold Ctrl/Cmd to add multiple levels.
          </p>
        </div>
      )}
    </div>
  );
};
